import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Badge, Empty, statusTone } from "@/components/admin/Ui";
import { Sparkle, EnvelopeSimple, Trash, MagnifyingGlass } from "@phosphor-icons/react";

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/leads", { params: q ? { q } : {} });
    setLeads(data.leads || []);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const score = async (id) => {
    setBusyId(id + ":score");
    try {
      const { data } = await api.post("/leads/score", { lead_id: id });
      setLeads((arr) => arr.map((l) => (l.id === id ? data.lead : l)));
      toast.success(`Scored: ${data.lead.score}/10 (${data.lead.fit_category})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scoring failed");
    } finally {
      setBusyId(null);
    }
  };

  const generate = async (id) => {
    setBusyId(id + ":gen");
    try {
      await api.post("/outreach/generate", { lead_id: id });
      toast.success("Outreach drafted — see Outreach tab");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setBusyId(null);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete lead?")) return;
    await api.delete(`/leads/${id}`);
    setLeads((arr) => arr.filter((l) => l.id !== id));
  };

  return (
    <div data-testid="admin-leads-page">
      <PageHeader
        title="Leads"
        subtitle="Pipeline"
        action={
          <div className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
              <input
                data-testid="leads-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Search"
                className="pl-8 pr-3 py-2 border border-black/15 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-navy"
              />
            </div>
            <button data-testid="leads-search-button" onClick={load} className="btn-secondary !py-2 !px-3">Search</button>
          </div>
        }
      />
      <div className="p-8">
        {loading ? <div className="text-navy/60">Loading…</div> :
         leads.length === 0 ? <Empty label="No leads yet. Try the Research Agent." /> : (
          <div className="bg-white border border-black/10 overflow-x-auto">
            <table className="w-full text-sm row-alt" data-testid="leads-table">
              <thead className="border-b border-black/10 text-left text-navy/60">
                <tr className="text-[10px] uppercase tracking-widest">
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Country</th>
                  <th className="px-4 py-3">Services</th>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-black/5" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-navy">{l.company_name}</div>
                      <div className="text-[11px] text-navy/50">{l.city}</div>
                    </td>
                    <td className="px-4 py-3 text-navy/80">{l.country}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(l.services || []).slice(0, 3).map((s) => (
                          <span key={s} className="text-[10px] uppercase tracking-widest border border-navy/20 px-1.5 py-0.5 text-navy/70">{s}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 ship-number font-bold text-navy">{l.score}</td>
                    <td className="px-4 py-3"><Badge tone={statusTone(l.status)}>{l.status}</Badge></td>
                    <td className="px-4 py-3 text-navy/70 text-xs">
                      {l.key_contact_email || l.email || "—"}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        data-testid={`lead-score-${l.id}`}
                        disabled={busyId === l.id + ":score"}
                        onClick={() => score(l.id)}
                        className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-navy hover:text-ember mr-3"
                      >
                        <Sparkle size={14} /> {busyId === l.id + ":score" ? "Scoring…" : "Score"}
                      </button>
                      <button
                        data-testid={`lead-generate-${l.id}`}
                        disabled={busyId === l.id + ":gen"}
                        onClick={() => generate(l.id)}
                        className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-navy hover:text-ember mr-3"
                      >
                        <EnvelopeSimple size={14} /> {busyId === l.id + ":gen" ? "Drafting…" : "Draft"}
                      </button>
                      <button
                        data-testid={`lead-delete-${l.id}`}
                        onClick={() => del(l.id)}
                        className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-red-600 hover:text-red-700"
                      >
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
